// useZones — reads the 7 GHMC zone sites from Firestore.
//
// Replaces the hardcoded `if (areaNum === 1) return 'Charminar'` ladder that
// used to live in 5+ files across the dashboard. Source of truth is now the
// mirrored `sites/` collection in the feedback Firebase project, which is
// synced nightly (and on-demand) from ixora-one/sites/. See:
//   apps/mobile/ixora-feedback-app/.../sync/syncSitesFromIxoraone.ts
//
// A "zone" is a root site (parentId == null) with an `areaNumber` set —
// only the seven GHMC sanitation zones have that field. See:
//   apps/mobile/ixoraone/functions/scripts/seed-ghmc-zones.mjs
//
// Returns: { zones, zoneByAreaNumber, zoneByCode, zoneById, isLoading, error }

import { useEffect, useState, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export function useZones() {
  const [zones, setZones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Single range filter avoids needing a composite index. There are only
    // ever ~7 zones; the active filter is applied client-side.
    const q = query(
      collection(db, 'sites'),
      where('areaNumber', '>=', 1),
      orderBy('areaNumber'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setZones(
          snap.docs
            .map((d) => ({
              id: d.id,
              code: d.data().code,
              name: d.data().name,
              areaNumber: d.data().areaNumber,
              region: d.data().region,
              parentId: d.data().parentId ?? null,
              active: d.data().active !== false,
            }))
            .filter((z) => z.active),
        );
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, []);

  const { zoneByAreaNumber, zoneByCode, zoneById } = useMemo(() => {
    const byNum = new Map();
    const byCode = new Map();
    const byId = new Map();
    for (const z of zones) {
      byNum.set(z.areaNumber, z);
      byCode.set(z.code, z);
      byId.set(z.id, z);
    }
    return { zoneByAreaNumber: byNum, zoneByCode: byCode, zoneById: byId };
  }, [zones]);

  return { zones, zoneByAreaNumber, zoneByCode, zoneById, isLoading, error };
}

// Resolves a feedback doc's legacy `shop_area` (string '1'..'7') to the
// matching zone. Returns undefined if no match.
export function resolveZoneFromShopArea(zoneByAreaNumber, shopArea) {
  if (shopArea == null || shopArea === '') return undefined;
  const n = typeof shopArea === 'number' ? shopArea : parseInt(shopArea, 10);
  if (!Number.isFinite(n)) return undefined;
  return zoneByAreaNumber.get(n);
}

// Resolves a supervisor's `ixora_allotment` (legacy string — either an area
// name like "Charminar" or a number string like "1") to the matching zone.
// Used during the dual-read window before `allotted_site_ids` is universal.
export function resolveZoneFromAllotment(zoneByAreaNumber, allotment) {
  if (!allotment) return undefined;
  const s = String(allotment).trim();
  if (!s) return undefined;
  // Try as area number first.
  const n = parseInt(s, 10);
  if (Number.isFinite(n)) {
    const byNum = zoneByAreaNumber.get(n);
    if (byNum) return byNum;
  }
  // Aliases the old UI accepted for the same canonical zone name.
  const ALIASES = {
    'Police Station': 'Police Stations - Hyderabad',
    'Police Station Hyderabad': 'Police Stations - Hyderabad',
    'Police Station Cyberabad': 'Police Stations - Cyberabad',
  };
  const canonical = ALIASES[s] ?? s;
  for (const z of zoneByAreaNumber.values()) {
    if (z.name.toLowerCase() === canonical.toLowerCase()) return z;
  }
  return undefined;
}

// Returns the supervisor's effective zone(s) — prefers the new
// `allotted_site_ids` array, falls back to the legacy `ixora_allotment`
// single string. Returns [] for the 'Manager' sentinel and unresolvable
// allotments. Array form supports future multi-zone supervisors.
export function getSupervisorZones(supervisor, zoneById, zoneByAreaNumber) {
  if (!supervisor) return [];
  const arr = supervisor.allotted_site_ids;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.map((id) => zoneById.get(id)).filter(Boolean);
  }
  const legacy = supervisor.ixora_allotment;
  if (!legacy) return [];
  if (String(legacy).trim().toLowerCase() === 'manager') return [];
  const z = resolveZoneFromAllotment(zoneByAreaNumber, legacy);
  return z ? [z] : [];
}

// Convenience: returns the supervisor's primary zone name, or null.
// Used by display sites that show a single zone label (most of the dashboard).
export function getSupervisorPrimaryZoneName(supervisor, zoneById, zoneByAreaNumber) {
  const zs = getSupervisorZones(supervisor, zoneById, zoneByAreaNumber);
  return zs.length > 0 ? zs[0].name : null;
}
